import { create } from 'zustand';
import type {
  BattleMap,
  MapTexture,
  MapGenerationPrompt,
  MapGenerationResult,
  MaterialZone,
  MapEnvironment,
  MapMood,
  MapStyle,
  SavedMapSummary,
} from '@machina/core';
import { trpc } from '../lib/trpc';
import { supabase } from '../lib/supabase';
import { createSubscription } from '../lib/realtimeHelpers';
import { useCampaignMapStore } from './campaignMapStore';

/**
 * Map Store
 *
 * Manages battle map state for the current scene.
 * Designed for Unreal Engine compatibility - data model uses world units (feet),
 * not pixels. The renderer (Pixi.js for now, Unreal later) converts to screen space.
 */

interface MapState {
  // Current active map (top-down view)
  activeMap: BattleMap | null;
  // Isometric map (separate from top-down — iso-native textures)
  activeIsoMap: BattleMap | null;

  // Map library (saved maps for this campaign)
  mapLibrary: Map<string, BattleMap>;

  // Generation state
  isGenerating: boolean;
  generationProgress: number; // 0-100
  generationError: string | null;
  libraryError: string | null;

  // Re-entrancy guard for requestMap()
  isRequestingMap: boolean;

  // Current generation prompt (for UI binding)
  currentPrompt: MapGenerationPrompt;

  // Render settings (for 2D preview)
  pixelsPerFoot: number; // Resolution for web preview
  zoom: number; // Canvas zoom level (1 = 100%)

  // Scene sync
  currentSceneId: string | null;

  // Actions - Map Management
  setActiveMap: (map: BattleMap | null) => void;
  setActiveIsoMap: (map: BattleMap | null) => void;
  createEmptyMap: (name: string, widthFeet: number, heightFeet: number) => BattleMap;
  updateMap: (mapId: string, updates: Partial<BattleMap>) => void;
  deleteMap: (mapId: string) => void;
  saveMapToLibrary: (map: BattleMap) => void;
  loadMapFromLibrary: (mapId: string) => void;

  // Actions - Generation
  setCurrentPrompt: (prompt: Partial<MapGenerationPrompt>) => void;
  generateMap: () => Promise<MapGenerationResult | null>;
  cancelGeneration: () => void;

  // Actions - Textures
  setBaseTexture: (texture: MapTexture) => void;
  addTextureLayer: (texture: MapTexture) => void;
  removeTextureLayer: (textureId: string) => void;

  // Actions - Material Zones
  addMaterialZone: (zone: MaterialZone) => void;
  updateMaterialZone: (zoneId: string, updates: Partial<MaterialZone>) => void;
  removeMaterialZone: (zoneId: string) => void;

  // Queries
  getMapById: (mapId: string) => BattleMap | undefined;
  getMapBoundsInPixels: () => { width: number; height: number } | null;

  // Zoom control
  setZoom: (zoom: number) => void;

  // Scene sync (Supabase persistence + realtime)
  realtimeCleanup: (() => void) | null;
  /** Explicit setter — pushed from useSupabaseSync on scene activation */
  setCurrentSceneId: (sceneId: string | null) => void;
  loadFromScene: (sceneId: string) => Promise<void>;
  saveToScene: () => Promise<boolean>;
  subscribeToSceneMap: (sceneId: string) => void;
  unsubscribeFromSceneMap: () => void;

  // Map Guardian — single entry point for all map operations
  requestMap: (request: MapRequest) => Promise<BattleMap | null>;

  // Saved Maps Library
  savedMaps: SavedMapSummary[];
  saveMapToDb: (name: string, description?: string, tags?: string[]) => Promise<string | null>;
  loadSavedMaps: (campaignId: string) => Promise<void>;
  loadSavedMapById: (mapId: string) => Promise<void>;
  deleteSavedMap: (mapId: string) => Promise<void>;

  // Campaign isolation — clear state without touching DB
  clearCampaignState: () => void;
}

// =============================================================================
// MAP REQUEST (Guardian entry point)
// =============================================================================

export type MapRequestSource = 'scene-backdrop' | 'quick-preset' | 'manual-generate' | 'realtime-sync';

export interface MapRequest {
  source: MapRequestSource;
  sceneId?: string;
  name?: string;
  widthFeet?: number;
  heightFeet?: number;
  environment?: MapEnvironment;
  mood?: MapMood;
  style?: MapStyle;
  prompt?: string;
  staticUrl?: string;
  features?: string[];
  avoid?: string[];
  encounterId?: string;
}

const DEFAULT_PROMPT: MapGenerationPrompt = {
  userPrompt: '',
  environment: 'dungeon_stone',
  style: 'top_down_tactical',
  mood: 'torchlit',
  widthFeet: 100,
  heightFeet: 100,
  features: [],
  avoid: [],
};

// Restore saved zoom from localStorage
function getSavedZoom(): number {
  try {
    const saved = localStorage.getItem('machina:zoom');
    if (saved) {
      const val = parseFloat(saved);
      if (!isNaN(val) && val >= 0.25 && val <= 4) return val;
    }
  } catch { /* ignore */ }
  return 1;
}

export const useMapStore = create<MapState>((set, get) => ({
  activeMap: null,
  activeIsoMap: null,
  mapLibrary: new Map(),
  isGenerating: false,
  isRequestingMap: false,
  generationProgress: 0,
  generationError: null,
  libraryError: null,
  currentPrompt: DEFAULT_PROMPT,
  pixelsPerFoot: 10, // 10px per foot = 50px per 5ft square
  zoom: getSavedZoom(),
  currentSceneId: null,
  setCurrentSceneId: (sceneId) => {
    const prev = get().currentSceneId;
    if (prev !== sceneId) {
      console.log(`[mapStore] currentSceneId: ${prev ?? 'null'} → ${sceneId ?? 'null'}`);
      set({ currentSceneId: sceneId });
    }
  },
  realtimeCleanup: null,
  savedMaps: [],

  // ==========================================================================
  // MAP MANAGEMENT
  // ==========================================================================

  setActiveMap: (map) => {
    set({ activeMap: map });
  },

  setActiveIsoMap: (map) => {
    set({ activeIsoMap: map });
  },

  createEmptyMap: (name, widthFeet, heightFeet) => {
    const map: BattleMap = {
      id: crypto.randomUUID(),
      name,
      bounds: {
        width: widthFeet,
        height: heightFeet,
        originX: 0,
        originY: 0,
      },
      gridType: 'square',
      gridSizeFeet: 5,
      textureLayers: [],
      materialZones: [],
      environment: 'dungeon_stone',
      mood: 'torchlit',
      style: 'top_down_tactical',
      generationHistory: [],
      walls: [],
      doors: [],
      tags: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    set({ activeMap: map });
    return map;
  },

  updateMap: (mapId, updates) => {
    const { activeMap, mapLibrary } = get();

    if (activeMap && activeMap.id === mapId) {
      set({
        activeMap: {
          ...activeMap,
          ...updates,
          updatedAt: new Date(),
        },
      });
    }

    const libraryMap = mapLibrary.get(mapId);
    if (libraryMap) {
      const newLibrary = new Map(mapLibrary);
      newLibrary.set(mapId, {
        ...libraryMap,
        ...updates,
        updatedAt: new Date(),
      });
      set({ mapLibrary: newLibrary });
    }
  },

  deleteMap: (mapId) => {
    const { activeMap, mapLibrary } = get();

    if (activeMap && activeMap.id === mapId) {
      set({ activeMap: null });
    }

    const newLibrary = new Map(mapLibrary);
    newLibrary.delete(mapId);
    set({ mapLibrary: newLibrary });
  },

  saveMapToLibrary: (map) => {
    set((state) => {
      const newLibrary = new Map(state.mapLibrary);
      newLibrary.set(map.id, map);
      return { mapLibrary: newLibrary };
    });
  },

  loadMapFromLibrary: (mapId) => {
    const map = get().mapLibrary.get(mapId);
    if (map) {
      set({ activeMap: map });
    }
  },

  // ==========================================================================
  // GENERATION
  // ==========================================================================

  setCurrentPrompt: (promptUpdates) => {
    set((state) => ({
      currentPrompt: { ...state.currentPrompt, ...promptUpdates },
    }));
  },

  generateMap: async () => {
    // Guard against re-entrant calls (e.g. from reactive effects)
    if (get().isGenerating) {
      console.warn('[MapStore] generateMap called while already generating — skipping');
      return null;
    }

    const { currentPrompt, activeMap } = get();

    set({
      isGenerating: true,
      generationProgress: 0,
      generationError: null,
    });

    try {
      console.log('[MapStore] Generating map with prompt:', currentPrompt);

      set({ generationProgress: 10 });

      // Call generation API via tRPC
      // Uses 'auto' which prefers FLUX (cheaper) > DALL-E > mock
      const result = await trpc.maps.generate.mutate({
        prompt: currentPrompt,
        provider: 'auto',
      });

      set({ generationProgress: 90 });

      if (!result.success) {
        throw new Error('Generation failed');
      }

      // Create texture from result
      const texture: MapTexture = {
        id: crypto.randomUUID(),
        colorUrl: result.imageUrl,
        bounds: {
          width: currentPrompt.widthFeet,
          height: currentPrompt.heightFeet,
          originX: 0,
          originY: 0,
        },
        pixelWidth: result.width || currentPrompt.widthFeet * 10,
        pixelHeight: result.height || currentPrompt.heightFeet * 10,
        pixelsPerFoot: 10,
        zIndex: 0,
        opacity: 1,
      };

      // Create generation result
      const generationResult: MapGenerationResult = {
        id: crypto.randomUUID(),
        prompt: currentPrompt,
        texture,
        model: result.model || 'dall-e-3',
        generatedAt: new Date(),
        generationTimeMs: result.generationTimeMs || 0,
        tags: [],
      };

      // Update or create map with new texture
      if (activeMap) {
        set({
          activeMap: {
            ...activeMap,
            baseTexture: texture,
            generationHistory: [...activeMap.generationHistory, generationResult],
            updatedAt: new Date(),
          },
          isGenerating: false,
          generationProgress: 100,
        });
      } else {
        // Create new map
        const newMap: BattleMap = {
          id: crypto.randomUUID(),
          name: `Generated Map - ${new Date().toLocaleDateString()}`,
          bounds: {
            width: currentPrompt.widthFeet,
            height: currentPrompt.heightFeet,
            originX: 0,
            originY: 0,
          },
          gridType: 'square',
          gridSizeFeet: 5,
          baseTexture: texture,
          textureLayers: [],
          materialZones: [],
          environment: currentPrompt.environment,
          mood: currentPrompt.mood,
          style: currentPrompt.style || 'top_down_tactical',
          generationHistory: [generationResult],
          walls: [],
          doors: [],
          tags: [],
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        const isIso = (currentPrompt.style || 'top_down_tactical') === 'isometric';
        set({
          ...(isIso ? { activeIsoMap: newMap } : { activeMap: newMap }),
          isGenerating: false,
          generationProgress: 100,
        });
      }

      // Auto-save to scene
      const saved = await get().saveToScene();
      if (!saved) console.warn('[MapStore] generateMap: saveToScene returned false');

      return generationResult;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      set({
        isGenerating: false,
        generationProgress: 0,
        generationError: message,
      });
      console.error('[MapStore] Generation error:', error);
      return null;
    }
  },

  cancelGeneration: () => {
    // TODO: Implement actual cancellation
    set({
      isGenerating: false,
      generationProgress: 0,
    });
  },

  // ==========================================================================
  // TEXTURES
  // ==========================================================================

  setBaseTexture: (texture) => {
    const { activeMap } = get();
    if (!activeMap) return;

    set({
      activeMap: {
        ...activeMap,
        baseTexture: texture,
        updatedAt: new Date(),
      },
    });
  },

  addTextureLayer: (texture) => {
    const { activeMap } = get();
    if (!activeMap) return;

    set({
      activeMap: {
        ...activeMap,
        textureLayers: [...activeMap.textureLayers, texture],
        updatedAt: new Date(),
      },
    });
  },

  removeTextureLayer: (textureId) => {
    const { activeMap } = get();
    if (!activeMap) return;

    set({
      activeMap: {
        ...activeMap,
        textureLayers: activeMap.textureLayers.filter((t) => t.id !== textureId),
        updatedAt: new Date(),
      },
    });
  },

  // ==========================================================================
  // MATERIAL ZONES
  // ==========================================================================

  addMaterialZone: (zone) => {
    const { activeMap } = get();
    if (!activeMap) return;

    set({
      activeMap: {
        ...activeMap,
        materialZones: [...activeMap.materialZones, zone],
        updatedAt: new Date(),
      },
    });
  },

  updateMaterialZone: (zoneId, updates) => {
    const { activeMap } = get();
    if (!activeMap) return;

    set({
      activeMap: {
        ...activeMap,
        materialZones: activeMap.materialZones.map((z) =>
          z.id === zoneId ? { ...z, ...updates } : z
        ),
        updatedAt: new Date(),
      },
    });
  },

  removeMaterialZone: (zoneId) => {
    const { activeMap } = get();
    if (!activeMap) return;

    set({
      activeMap: {
        ...activeMap,
        materialZones: activeMap.materialZones.filter((z) => z.id !== zoneId),
        updatedAt: new Date(),
      },
    });
  },

  // ==========================================================================
  // QUERIES
  // ==========================================================================

  getMapById: (mapId) => {
    const { activeMap, mapLibrary } = get();
    if (activeMap && activeMap.id === mapId) return activeMap;
    return mapLibrary.get(mapId);
  },

  getMapBoundsInPixels: () => {
    const { activeMap, pixelsPerFoot } = get();
    if (!activeMap) return null;

    return {
      width: activeMap.bounds.width * pixelsPerFoot,
      height: activeMap.bounds.height * pixelsPerFoot,
    };
  },

  // ==========================================================================
  // ZOOM CONTROL
  // ==========================================================================

  setZoom: (zoom) => {
    // Clamp zoom between 0.25 and 4
    const clampedZoom = Math.max(0.25, Math.min(4, zoom));
    set({ zoom: clampedZoom });
    try { localStorage.setItem('machina:zoom', String(clampedZoom)); } catch { /* ignore */ }
  },

  // ==========================================================================
  // SCENE SYNC (SUPABASE PERSISTENCE)
  // ==========================================================================

  loadFromScene: async (sceneId: string) => {
    try {
      console.log('[MapStore] Loading map from scene:', sceneId);
      set({ currentSceneId: sceneId });

      const { data, error } = await supabase
        .from('machina_scenes')
        .select('map_data')
        .eq('id', sceneId)
        .single();

      if (error) {
        console.error('[MapStore] Failed to load scene:', error);
        return;
      }

      const mapData = data?.map_data as BattleMap | null;

      if (mapData && mapData.id) {
        // Restore dates from ISO strings
        const restoredMap: BattleMap = {
          ...mapData,
          createdAt: new Date(mapData.createdAt),
          updatedAt: new Date(mapData.updatedAt),
          generationHistory: mapData.generationHistory?.map(h => ({
            ...h,
            generatedAt: new Date(h.generatedAt),
          })) || [],
        };

        set({ activeMap: restoredMap });
        console.log('[MapStore] Loaded map:', restoredMap.name);
      } else {
        console.log('[MapStore] No map data in scene');
        set({ activeMap: null });
      }
    } catch (err) {
      console.error('[MapStore] Error loading from scene:', err);
    }
  },

  saveToScene: async () => {
    const { activeMap, currentSceneId } = get();

    if (!currentSceneId) {
      console.warn('[MapStore] No scene ID set, cannot save');
      return false;
    }

    if (!activeMap) {
      console.warn('[MapStore] No active map to save');
      return false;
    }

    try {
      console.log('[MapStore] Saving map to scene:', currentSceneId);

      const { error } = await supabase
        .from('machina_scenes')
        .update({
          map_data: activeMap,
          updated_at: new Date().toISOString(),
        })
        .eq('id', currentSceneId);

      if (error) {
        console.error('[MapStore] Failed to save map:', error);
        set({ libraryError: 'Map not saved to scene — may be lost on reload' });
        return false;
      }

      console.log('[MapStore] Map saved successfully');
      return true;
    } catch (err) {
      console.error('[MapStore] Error saving to scene:', err);
      set({ libraryError: 'Map not saved to scene — may be lost on reload' });
      return false;
    }
  },

  // ==========================================================================
  // REALTIME SUBSCRIPTION — pushes map updates to all clients
  // ==========================================================================

  subscribeToSceneMap: (sceneId: string) => {
    // Clean up any existing subscription
    get().unsubscribeFromSceneMap();

    console.log('[MapStore] Subscribing to scene map changes:', sceneId);

    const { cleanup } = createSubscription({
      channelName: `map-sync-${sceneId}`,
      table: 'machina_scenes',
      filter: `id=eq.${sceneId}`,
      event: 'UPDATE',
      onPayload: (payload) => {
        // Don't overwrite if we're the one generating (GM client)
        if (get().isGenerating) return;

        // Supabase Realtime has a ~1MB payload limit. Map data with base64
        // textures often exceeds this, causing the event to be silently dropped
        // or arrive with truncated/null data. Instead of parsing the payload,
        // use the event as a notification and fetch the full data via REST.
        const row = payload.new as unknown as { id: string; map_data: BattleMap | null } | null;
        const mapData = row?.map_data;

        if (mapData && mapData.id && mapData.baseTexture?.colorUrl) {
          // Payload arrived intact (small map or within limits) — use directly
          const restoredMap: BattleMap = {
            ...mapData,
            createdAt: new Date(mapData.createdAt),
            updatedAt: new Date(mapData.updatedAt),
            generationHistory: mapData.generationHistory?.map(h => ({
              ...h,
              generatedAt: new Date(h.generatedAt),
            })) || [],
          };

          console.log('[MapStore] Realtime map update (inline):', restoredMap.name);
          set({ activeMap: restoredMap });

          // Auto-switch to battle mode when a map with a texture arrives
          if (restoredMap.baseTexture) {
            // Preserve iso if user's already there; only promote from the
            // campaign overview into a tactical mode.
            const currentMode = useCampaignMapStore.getState().mapMode;
            if (currentMode === 'campaign') {
              console.log('[MapStore] Promoting campaign → battle mode for new map');
              useCampaignMapStore.getState().setMapMode('battle');
            }
          }
        } else {
          // Payload truncated or map cleared — fetch full data via REST
          console.log('[MapStore] Realtime event received, fetching full map via REST');
          get().loadFromScene(sceneId).then(() => {
            const loaded = get().activeMap;
            if (loaded?.baseTexture) {
              const currentMode = useCampaignMapStore.getState().mapMode;
              if (currentMode !== 'battle') {
                console.log('[MapStore] Auto-switching to battle mode for new map');
                useCampaignMapStore.getState().setMapMode('battle');
              }
            }
          }).catch((err) => {
            console.error('[MapStore] Realtime REST fallback failed:', err);
          });
        }
      },
    });

    set({ realtimeCleanup: cleanup });
  },

  unsubscribeFromSceneMap: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },

  // ==========================================================================
  // MAP GUARDIAN — single entry point for all map lifecycle operations
  // ==========================================================================

  requestMap: async (request) => {
    // Guard: reject if already generating or another request is in flight
    if (get().isRequestingMap || get().isGenerating) {
      console.warn('[MapStore] requestMap rejected — already in progress');
      return null;
    }

    set({ isRequestingMap: true });

    try {
      const {
        source,
        name = 'Battle Map',
        widthFeet = 100,
        heightFeet = 100,
        environment = 'dungeon_stone' as MapEnvironment,
        mood = 'torchlit' as MapMood,
        style = 'top_down_tactical' as MapStyle,
        prompt: userPrompt = '',
        staticUrl,
        features = [],
        avoid = [],
      } = request;

      console.log(`[MapStore] requestMap from ${source}:`, name);

      // Sync scene ID from request so saveToScene() works for module sessions
      if (request.sceneId && !get().currentSceneId) {
        set({ currentSceneId: request.sceneId });
      }

      // Preserve iso if user's already there; only promote from campaign overview.
      const currentMode = useCampaignMapStore.getState().mapMode;
      if (currentMode === 'campaign') {
        useCampaignMapStore.getState().setMapMode('battle');
      }

      // Case 1: Static URL — no generation needed
      if (staticUrl) {
        const map = get().createEmptyMap(name, widthFeet, heightFeet);
        const texture: MapTexture = {
          id: crypto.randomUUID(),
          colorUrl: staticUrl,
          bounds: { width: widthFeet, height: heightFeet, originX: 0, originY: 0 },
          pixelWidth: widthFeet * 10,
          pixelHeight: heightFeet * 10,
          pixelsPerFoot: 10,
          zIndex: 0,
          opacity: 1,
        };
        const finalMap: BattleMap = { ...map, environment, mood, baseTexture: texture };
        // Clear the opposite slot so the iso ground plane doesn't show a stale map
        // from a previous scene (getIsoMap reads activeIsoMap ?? activeMap).
        const isIsoStatic = style === 'isometric';
        set(isIsoStatic ? { activeIsoMap: finalMap, activeMap: null } : { activeMap: finalMap, activeIsoMap: null });
        const saved = await get().saveToScene();
        if (!saved) console.warn('[MapStore] requestMap(static): saveToScene returned false');
        return finalMap;
      }

      // Case 2: Scene has saved map data — load from DB
      if (request.sceneId && source === 'scene-backdrop') {
        try {
          const { data } = await supabase
            .from('machina_scenes')
            .select('map_data')
            .eq('id', request.sceneId)
            .single();

          const mapData = data?.map_data as BattleMap | null;
          if (mapData?.id && mapData?.baseTexture?.colorUrl) {
            // Cache compat check — accept cached map if:
            // - cached style matches requested, OR
            // - cached style is undefined/legacy AND caller is not iso (treat unknown as top-down)
            // - map is explicitly locked (future: preserveMap flag on scene)
            const cachedStyle = mapData.style;
            const wantIso = style === 'isometric';
            const cachedIsIso = cachedStyle === 'isometric';
            const cachedIsKnownTopDown = cachedStyle === 'top_down_tactical' || cachedStyle === 'top_down_painted';
            // If cached style is undefined/unknown and user wants iso, regen. Otherwise accept.
            const compatible = (wantIso && cachedIsIso) || (!wantIso && (cachedIsKnownTopDown || !cachedStyle));
            if (!compatible) {
              console.log(`[MapStore] Cached scene map style='${cachedStyle ?? 'unknown'}' incompatible with requested '${style}' — regenerating`);
            } else {
              const restoredMap: BattleMap = {
                ...mapData,
                createdAt: new Date(mapData.createdAt),
                updatedAt: new Date(mapData.updatedAt),
                generationHistory: mapData.generationHistory?.map(h => ({
                  ...h,
                  generatedAt: new Date(h.generatedAt),
                })) || [],
              };
              set(wantIso ? { activeIsoMap: restoredMap, activeMap: null } : { activeMap: restoredMap, activeIsoMap: null });
              console.log(`[MapStore] Preserved cached map for scene (${wantIso ? 'iso' : 'top-down'} slot):`, restoredMap.name);
              return restoredMap;
            }
          }
        } catch {
          // Fall through to generation
        }
      }

      // Case 3: Generate new map — route to iso or top-down slot
      const map = get().createEmptyMap(name, widthFeet, heightFeet);
      const isIso = style === 'isometric';
      const mapWithMeta = { ...map, environment, mood, style };
      // Also null the opposite slot so old scene maps don't shadow this one
      set(isIso ? { activeIsoMap: mapWithMeta, activeMap: null } : { activeMap: mapWithMeta, activeIsoMap: null });

      get().setCurrentPrompt({
        environment,
        style,
        mood,
        userPrompt,
        widthFeet,
        heightFeet,
        features,
        avoid,
      });

      await get().generateMap();
      // Return whichever slot got populated — iso generations live in activeIsoMap.
      return get().activeIsoMap ?? get().activeMap;
    } finally {
      set({ isRequestingMap: false });
    }
  },

  // ==========================================================================
  // SAVED MAPS LIBRARY
  // ==========================================================================

  saveMapToDb: async (name, description, tags) => {
    set({ libraryError: null });
    const { activeMap } = get();
    if (!activeMap) {
      console.warn('[MapStore] No active map to save');
      return null;
    }

    // Try activeMap.campaignId, but callers may also pass campaignId via the store externally
    // We'll look it up from the campaign store as fallback
    let campaignId = activeMap.campaignId;
    if (!campaignId) {
      // Dynamic import avoidance — access via window or direct import
      try {
        const { useCampaignStore } = await import('./campaignStore');
        campaignId = useCampaignStore.getState().currentCampaignId ?? undefined;
      } catch { /* ignore */ }
    }
    if (!campaignId) {
      console.warn('[MapStore] No campaignId available');
      set({ libraryError: 'No campaign selected — cannot save map' });
      return null;
    }

    try {
      const result = await trpc.maps.save.mutate({
        campaignId,
        name,
        description,
        mapData: activeMap as unknown as { id: string; bounds: { width: number; height: number } },
        tags: tags ?? [],
      });

      // Refresh list
      await get().loadSavedMaps(campaignId);
      return result.mapId;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save map';
      console.error('[MapStore] Failed to save map:', err);
      set({ libraryError: message });
      return null;
    }
  },

  loadSavedMaps: async (campaignId) => {
    try {
      const result = await trpc.maps.list.query({ campaignId });
      set({ savedMaps: result.maps });
    } catch (err) {
      console.error('[MapStore] Failed to load saved maps:', err);
    }
  },

  loadSavedMapById: async (mapId) => {
    set({ libraryError: null });
    try {
      const result = await trpc.maps.load.query({ mapId });
      const mapData = result.mapData as unknown as BattleMap;

      if (!mapData?.id) {
        console.warn('[MapStore] Loaded map has no ID');
        set({ libraryError: 'Loaded map data is invalid' });
        return;
      }

      // Restore dates
      const restoredMap: BattleMap = {
        ...mapData,
        createdAt: new Date(mapData.createdAt),
        updatedAt: new Date(mapData.updatedAt),
        generationHistory: mapData.generationHistory?.map(h => ({
          ...h,
          generatedAt: new Date(h.generatedAt),
        })) ?? [],
      };

      // Preserve iso if user's already there; only promote from campaign overview.
      const currentMode = useCampaignMapStore.getState().mapMode;
      if (currentMode === 'campaign') {
        useCampaignMapStore.getState().setMapMode('battle');
      }

      set({ activeMap: restoredMap });
      const saved = await get().saveToScene();
      if (!saved) console.warn('[MapStore] loadSavedMapById: saveToScene returned false');
      console.log('[MapStore] Loaded saved map:', restoredMap.name);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load map';
      console.error('[MapStore] Failed to load saved map:', err);
      set({ libraryError: message });
    }
  },

  deleteSavedMap: async (mapId) => {
    set({ libraryError: null });
    try {
      await trpc.maps.delete.mutate({ mapId });
      set((state) => ({
        savedMaps: state.savedMaps.filter((m) => m.id !== mapId),
      }));
      console.log('[MapStore] Deleted saved map:', mapId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete map';
      console.error('[MapStore] Failed to delete saved map:', err);
      set({ libraryError: message });
    }
  },

  clearCampaignState: () => {
    set({
      activeMap: null,
      activeIsoMap: null,
      mapLibrary: new Map(),
      savedMaps: [],
      currentSceneId: null,
      isGenerating: false,
      isRequestingMap: false,
      generationError: null,
      libraryError: null,
      currentPrompt: DEFAULT_PROMPT,
    });
  },
}));

// Expose for dev tools and Playwright capture scripts
if (typeof window !== 'undefined') {
  (window as any).__mapStore = useMapStore;
}
